import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

/**
 * Translation coverage tiers (computed against the EN baseline of ~410 leaf keys
 * in src/lib/i18n.ts — see /tmp/i18n_audit.mjs for the audit script):
 *   - stable  : ≥50% coverage. Renders well across most surfaces.
 *   - partial : 20%-49%. Common UI works; many pages fall back to EN.
 *   - beta    : <20% coverage OR no resource bundle present. Shown with a
 *               BETA badge so users understand most strings will appear in EN
 *               (via i18n fallbackLng='en'). Selecting these languages still
 *               works — they translate the keys that exist and fall back for
 *               the rest. We do not hide them so existing user preferences
 *               keep working.
 *
 * Update this table when you ship more translations. Source of truth is the
 * audit script, NOT a guess.
 */
type Coverage = 'stable' | 'partial' | 'beta';

const SUPPORTED_LANGUAGES: ReadonlyArray<{
  code: string;
  name: string;
  flag: string;
  coverage: Coverage;
}> = [
  // Global
  { code: 'en', name: 'English', flag: '🇺🇸', coverage: 'stable' },
  // African Languages
  { code: 'sw', name: 'Kiswahili', flag: '🌍', coverage: 'stable' },          // 258/410 ≈ 63%
  { code: 'fr', name: 'Français', flag: '🇫🇷', coverage: 'stable' },         // 446 keys (incl. extras)
  { code: 'so', name: 'Af Soomaali', flag: '🇸🇴', coverage: 'stable' },     // 358/410 ≈ 87%
  { code: 'pt', name: 'Português', flag: '🇲🇿', coverage: 'partial' },     // see audit
  { code: 'ar', name: 'العربية', flag: '🇸🇦', coverage: 'partial' },        // 190/410 ≈ 46%
  { code: 'af', name: 'Afrikaans', flag: '🇿🇦', coverage: 'partial' },
  { code: 'zu', name: 'isiZulu', flag: '🇿🇦', coverage: 'partial' },
  { code: 'pcm', name: 'Naijá (Pidgin)', flag: '🇳🇬', coverage: 'partial' }, // 159/410 ≈ 39%
  { code: 'ha', name: 'Hausa', flag: '🇳🇬', coverage: 'beta' },              // 108/410 ≈ 26%
  { code: 'ig', name: 'Igbo', flag: '🇳🇬', coverage: 'beta' },               // 108/410 ≈ 26%
  { code: 'ny', name: 'Chichewa', flag: '🇲🇼', coverage: 'beta' },
  { code: 'mg', name: 'Malagasy', flag: '🇲🇬', coverage: 'beta' },
  { code: 'xh', name: 'isiXhosa', flag: '🇿🇦', coverage: 'beta' },
  { code: 'rw', name: 'Ikinyarwanda', flag: '🇷🇼', coverage: 'beta' },
  { code: 'am', name: 'አማርኛ', flag: '🇪🇹', coverage: 'beta' },              // Ethiopia (AHRI)
  { code: 'lg', name: 'Luganda', flag: '🇺🇬', coverage: 'beta' },            // Uganda (MCRI)
  { code: 'luo', name: 'Dholuo', flag: '🇰🇪', coverage: 'beta' },            // Western Kenya (K-IRDO / Kisumu OPTIMISE-TB)
  // Asian Languages — stub bundles only (~5% coverage)
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', coverage: 'partial' },
  { code: 'th', name: 'ภาษาไทย', flag: '🇹🇭', coverage: 'beta' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', coverage: 'beta' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', coverage: 'beta' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾', coverage: 'beta' },
] as const;

interface LanguageSwitcherProps {
  variant?: 'icon' | 'full';
  className?: string;
}

export function LanguageSwitcher({ variant = 'icon', className = '' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('ptc-language', langCode);
  };

  const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === 'icon' ? 'icon' : 'sm'}
          className={`h-9 ${variant === 'full' ? 'gap-2' : 'w-9'} ${className}`}
          aria-label="Change language"
        >
          <Globe className="h-4 w-4" />
          {variant === 'full' && (
            <span className="text-sm">{currentLanguage.flag} {currentLanguage.code.toUpperCase()}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-popover border border-border z-[100]">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
              {lang.coverage === 'partial' && (
                <span
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                  title="Partial translation — some sections will appear in English."
                >
                  partial
                </span>
              )}
              {lang.coverage === 'beta' && (
                <span
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400"
                  title="Beta translation — most of the site will appear in English while we expand coverage."
                >
                  beta
                </span>
              )}
            </span>
            {currentLang === lang.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
